import { PushPin } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useCurrentDashboard } from "components/Signals/useCurrentDashboard";
import { observer } from "mobx-react";
import React from "react";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { useStore } from "utils/useStore";

const pinnedSx = (theme: Theme) => ({
    color: theme.palette.primary.main,
    rotate: "45deg",
    "&.Mui-disabled": { color: theme.palette.primary.main },
});

const unpinnedSx = (theme: Theme) => ({
    color: theme.palette.action.active,
    rotate: "45deg",
    "&.Mui-disabled": { color: theme.palette.action.disabled },
});

export const PinFilterButton = observer(() => {
    const currentDashboard = useCurrentDashboard();
    const applicationFiltersStore = useStore(ApplicationFiltersStore);

    if (!currentDashboard) return null;

    const activeSavedFilter =
        applicationFiltersStore.selectedSavedFilter ??
        applicationFiltersStore.getSavedFilterById(
            currentDashboard.pinnedApplicationFilterId,
        );

    const isEditable = currentDashboard.dashboardIsEditable;
    const isPinned =
        !!activeSavedFilter &&
        currentDashboard.pinnedApplicationFilterId === activeSavedFilter.id;

    // Viewer-only: show applied pin if one exists, otherwise hide entirely
    if (!isEditable) {
        if (!currentDashboard.pinnedApplicationFilterId) return null;
        return (
            <IconButton disabled sx={pinnedSx}>
                <PushPin />
            </IconButton>
        );
    }

    // Owner/Editor: always show
    const hasNoFilterSelected = !activeSavedFilter;
    const isLoading = currentDashboard.getTaskLoading("Pin Filter");

    const button = (
        <IconButton
            onClick={() => {
                if (activeSavedFilter) {
                    currentDashboard.toggleCurrentDashboardPinnedFilter(
                        activeSavedFilter.id,
                    );
                }
            }}
            disabled={hasNoFilterSelected || isLoading}
            sx={isPinned ? pinnedSx : unpinnedSx}
        >
            <PushPin />
        </IconButton>
    );

    if (hasNoFilterSelected) {
        return (
            <Tooltip title="Apply a saved filter in order to pin.">
                <span>{button}</span>
            </Tooltip>
        );
    }

    return button;
});
